import { Fragment, type ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'default';
  loading?: boolean;
  icon?: ReactNode;
}

const variantStyles = {
  danger: {
    iconBg: 'bg-error/10',
    iconColor: 'text-error',
    buttonVariant: 'danger' as const,
  },
  warning: {
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
    buttonVariant: 'primary' as const,
  },
  default: {
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    buttonVariant: 'primary' as const,
  },
};

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  loading = false,
  icon,
}: ConfirmDialogProps) {
  const styles = variantStyles[variant];

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform rounded-lg bg-background-subtle p-6 shadow-xl transition-all">
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}
                  >
                    {icon || <AlertTriangle className={`h-5 w-5 ${styles.iconColor}`} />}
                  </div>
                  <div className="flex-1">
                    <Dialog.Title className="text-base font-semibold text-foreground">
                      {title}
                    </Dialog.Title>
                    {description && (
                      <div className="mt-2 text-sm text-foreground-muted">
                        {description}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <Button variant="secondary" onClick={onClose} disabled={loading}>
                    {cancelText}
                  </Button>
                  <Button
                    variant={styles.buttonVariant}
                    onClick={onConfirm}
                    loading={loading}
                  >
                    {confirmText}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
